import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { env } from '@/utils/constants/env';
import useAuthState from '@/states/auth.state';
import useTenantState from '@/states/tenant.state';

declare module 'axios' {
  export interface AxiosDefaults {
    cloudStorage?: string;
  }
}

export const axiosTasklist: AxiosInstance = axios.create({
  baseURL: env.apiTasklistBaseUrl,
});

axiosTasklist.defaults.cloudStorage = env.cloudStorage;

axiosTasklist.interceptors.request.use(config => {
  const token = useAuthState.getState().token;
  const activeTenant = useTenantState.getState().currentTenant;

  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (activeTenant) config.headers['X-ACTIVE-TENANT'] = activeTenant.slug;

  return config;
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });

  failedQueue = [];
};

axiosTasklist.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      const useErrorState = (await import('@/states/error.state')).default;
      useErrorState.getState().set403Error(true);
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return axiosTasklist(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const rejectedToken = String(originalRequest.headers?.Authorization ?? '').replace(/^Bearer /, '') || undefined;
        const newToken = await useAuthState.getState().getToken(rejectedToken);

        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue();
          return axiosTasklist(originalRequest);
        } else {
          processQueue(new Error('Token refresh failed'));
          await useAuthState.getState().logout({ revoke: false });
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // getToken hanya melempar untuk kegagalan sementara (jaringan/5xx); sesi tetap dipertahankan
        processQueue(refreshError as Error);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);
